import { useMemo, useState } from 'react'

import AppBar from '@mui/material/AppBar'
import Toolbar from '@mui/material/Toolbar'
import Typography from '@mui/material/Typography'
import Avatar from '@mui/material/Avatar'
import SmartToy from '@mui/icons-material/SmartToy'
import PhotoCameraOutlined from '@mui/icons-material/PhotoCameraOutlined'
import ContentCopyOutlined from '@mui/icons-material/ContentCopyOutlined'
import ArrowForward from '@mui/icons-material/ArrowForward'
import Refresh from '@mui/icons-material/Refresh'
import PlayArrow from '@mui/icons-material/PlayArrow'
import { Play } from 'lucide-react'

import { getGuiSessionConfig } from '../../workspace/config/guiSessionConfig'
import ReplayTaskDescription from '../../workspaceReplay2/components/ReplayTaskDescription'
import ReplayTestCases from '../../workspaceReplay2/components/ReplayTestCases'
import ReplayCodeEditor from '../../workspaceReplay2/components/ReplayCodeEditor'
import hokieLogo from '../../../icon/hokie-logo.png'

import '../../workspace/css/workspace.scss'
import '../../../css/studentview-workspace.scss'

// Read-only, context-free preview of what a student sees on /workspace/:id for a GUI
// (web-dev) session type. It mirrors Workspace.jsx's render tree for a fresh student who
// has not interacted yet: default panel sizes from usePanelResize (left/right 450px,
// terminal 250px, both splits 50%), the Live Preview tab selected, manual-preview panes
// not yet rendered, the terminal empty, and the chat showing only its welcome message.
// Live leaves that read app contexts or log telemetry (TaskDescription, TestCases,
// Terminal, CodeEditor, ChatWindow, LivePreview, Navbar) are replaced by the
// context-free replay leaves or by markup copied from those components.

// usePanelResize.js initial state.
export const LEFT_WIDTH = 450
export const RIGHT_WIDTH = 450
export const TERMINAL_HEIGHT = 250
export const IDE_SPLIT_PERCENT = 50
export const JS_VIEW_SPLIT_PERCENT = 50

export const ALL_WEB_DEV_FILES = ['html', 'css', 'js']
export const WEB_DEV_FILE_LABELS = { html: 'HTML', css: 'CSS', js: 'JavaScript' }
const EDITOR_LANGUAGE = { html: 'html', css: 'css', js: 'javascript' }

// ChatContext.js's WELCOME_MESSAGE text.
export const WELCOME_TEXT = 'Hey I am your AI assistant, feel free to ask me questions if you are stuck!'

export const parseFiles = (json) => {
  try {
    const parsed = JSON.parse(json || '')
    if (parsed && typeof parsed === 'object') {
      return { html: parsed.html || '', css: parsed.css || '', js: parsed.js || '' }
    }
  } catch (e) {
    // Malformed starter code - render blank, same as Workspace.
  }
  return { html: '', css: '', js: '' }
}

// Same document LivePreview.jsx builds (base reset, CSP, student css/html, js in a
// try/catch) minus its telemetry/snapshot instrumentation and parent postMessage calls.
const buildSrcDoc = ({ html = '', css = '', js = '' }) => `
<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <meta http-equiv="Content-Security-Policy" content="default-src 'none'; script-src 'unsafe-inline'; style-src 'unsafe-inline'; img-src https: data:; form-action 'none';">
  <title>Live Preview</title>
  <style>
    html, body {
      margin: 0;
      padding: 16px;
      font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
      background-color: #ffffff;
      color: #171717;
    }
    ${css}
  </style>
</head>
<body>
  ${html}
  <script>
    try {
      ${js}
    } catch (err) {}
  </script>
</body>
</html>
`

// Without a handler a header/tab button is inert (the read-only preview); with one it
// works (the wizard's interactive preview, live/InteractiveWorkspacePreview.jsx).
const buttonProps = (onClick) => (onClick ? { onClick } : { tabIndex: -1 })

// ---------------------------------------------------------------------------------------
// LivePreview.jsx replica. `files` given => rendered iframe (a reference pane that always
// renders); `files` omitted => the manual-mode placeholder a student sees before pressing
// the Render button. `renderLabel` given => the Render button is shown (real: onRender).
// ---------------------------------------------------------------------------------------

// LivePreview.jsx's header bar: plain title/badge, or the browser-chrome look, plus its
// Render and Refresh buttons. onRender/onRefresh are optional and only make the buttons
// live; they do not change what is drawn.
export function PreviewHeader({
  renderLabel = null,
  title = 'Web Preview',
  badge = 'Interactive',
  showRefresh = true,
  chromeStyle = false,
  onRender,
  onRefresh,
}) {
  return (
    <div style={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      padding: chromeStyle ? '8px 12px' : '10px 16px',
      gap: '10px',
      borderBottom: '1px solid #e5e5e5',
      backgroundColor: chromeStyle ? '#f3f4f6' : '#ffffff'
    }}>
      {chromeStyle ? (
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flex: 1, minWidth: 0 }}>
          <span aria-hidden="true" style={{ color: '#c7cbd1', fontSize: '1rem', userSelect: 'none' }}>‹</span>
          <span aria-hidden="true" style={{ color: '#c7cbd1', fontSize: '1rem', userSelect: 'none' }}>›</span>
          <div style={{
            flex: 1, minWidth: 0, display: 'flex', alignItems: 'center', gap: '6px',
            backgroundColor: '#ffffff', border: '1px solid #e5e5e5', borderRadius: '6px',
            padding: '4px 10px', overflow: 'hidden',
          }}>
            <span style={{ fontSize: '0.82rem', fontWeight: 600, color: '#171717', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {title}
            </span>
            {badge && (
              <span style={{ fontSize: '0.7rem', color: '#9ca3af', whiteSpace: 'nowrap' }}>
                {badge}
              </span>
            )}
          </div>
        </div>
      ) : (
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <span style={{
            fontFamily: "'Open Sans', sans-serif",
            fontWeight: 600,
            fontSize: '0.9rem',
            color: '#171717'
          }}>
            {title}
          </span>
          {badge && (
            <span style={{
              fontSize: '0.75rem',
              padding: '2px 6px',
              borderRadius: '4px',
              backgroundColor: '#f3f4f6',
              color: '#4b5563'
            }}>
              {badge}
            </span>
          )}
        </div>
      )}
      <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexShrink: 0 }}>
        {renderLabel && (
          <button
            type="button"
            {...buttonProps(onRender)}
            style={{
              display: 'flex', alignItems: 'center', gap: '4px', padding: '6px 10px',
              border: '1px solid #2563eb', borderRadius: '5px', background: '#2563eb',
              color: '#ffffff', cursor: 'pointer', fontSize: '0.78rem', fontWeight: 600,
            }}
          >
            <PlayArrow style={{ fontSize: '1rem' }} />
            {renderLabel}
          </button>
        )}
        {showRefresh && (
          <button
            type="button"
            {...buttonProps(onRefresh)}
            title="Reload the last rendered preview"
            style={{
              background: 'none', border: 'none', color: '#737373', cursor: 'pointer',
              padding: '4px', borderRadius: '4px', display: 'flex', alignItems: 'center',
              justifyContent: 'center', transition: 'background-color 0.2s'
            }}
          >
            <Refresh style={{ fontSize: '1.25rem' }} />
          </button>
        )}
      </div>
    </div>
  )
}

function PreviewPane({
  files = null,
  renderLabel = null,
  title = 'Web Preview',
  badge = 'Interactive',
  showRefresh = true,
  chromeStyle = false,
}) {
  const srcDoc = useMemo(() => (files ? buildSrcDoc(files) : ''), [files])

  return (
    <div className="ws-chat-container" style={{ display: 'flex', flexDirection: 'column', height: '100%', borderLeft: '1px solid #e5e5e5' }}>
      <PreviewHeader
        renderLabel={renderLabel}
        title={title}
        badge={badge}
        showRefresh={showRefresh}
        chromeStyle={chromeStyle}
      />

      <div style={{
        flex: 1,
        position: 'relative',
        background: '#ffffff',
        display: 'flex',
        justifyContent: 'center',
      }}>
        {srcDoc ? (
          <iframe
            srcDoc={srcDoc}
            title="VizGroup Live Web Preview"
            tabIndex={-1}
            // Scripts stay on (the real reference panes run the starter/target JS), but no
            // allow-modals/allow-forms: nothing in a preview should be able to pop a dialog.
            sandbox="allow-scripts"
            style={{
              width: '100%',
              height: '100%',
              border: 'none',
              background: '#ffffff',
              pointerEvents: 'none',
            }}
          />
        ) : (
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            height: '100%',
            color: '#737373',
            fontSize: '0.875rem'
          }}>
            {`Select ${renderLabel || 'Render'} to preview your page.`}
          </div>
        )}
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------------------
// CodeEditor.jsx replica (isWebDev). Chrome copied from CodeEditor; the CodeMirror body is
// the context-free, read-only ReplayCodeEditor.
// ---------------------------------------------------------------------------------------
function EditorBody({ file, code, editorKey }) {
  return (
    <div className="svw-code-body">
      <ReplayCodeEditor key={editorKey} code={code} language={EDITOR_LANGUAGE[file]} />
    </div>
  )
}

function ResetTemplateButton({ columns }) {
  return (
    <button
      type="button"
      tabIndex={-1}
      style={{
        padding: '4px 8px',
        fontSize: '0.725rem',
        fontWeight: 600,
        border: '1px solid #d1d5db',
        borderRadius: '4px',
        backgroundColor: '#ffffff',
        color: '#374151',
        cursor: 'pointer',
        outline: 'none',
        ...(columns ? {} : { marginRight: '8px', marginBottom: '4px' }),
        transition: 'all 0.15s ease'
      }}
    >
      Reset Template
    </button>
  )
}

function TabbedEditor({ keyPrefix, files, enabledFiles, hideDisabledTabs = false, extraReadOnlyFiles = [], paneTitle = null }) {
  const activeTab = enabledFiles[0] || 'html'
  const tabs = hideDisabledTabs
    ? ALL_WEB_DEV_FILES.filter((f) => enabledFiles.includes(f) || extraReadOnlyFiles.includes(f))
    : ALL_WEB_DEV_FILES

  return (
    <div className="ws-editor-container" style={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>
      <div className="ws-editor-tabs" style={{
        display: 'flex',
        background: '#fafafa',
        borderBottom: '1px solid #e5e5e5',
        padding: '6px 8px 0 8px',
        gap: '4px',
        justifyContent: 'space-between',
        alignItems: 'center'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          {paneTitle && (
            <span style={{
              fontSize: '0.95rem',
              fontWeight: 700,
              color: '#171717',
              paddingBottom: '6px',
              whiteSpace: 'nowrap',
            }}>
              {paneTitle}
            </span>
          )}
          <div style={{ display: 'flex', gap: '4px' }}>
            {tabs.map((tab) => {
              const readOnly = !enabledFiles.includes(tab)
              return (
                <button
                  key={tab}
                  type="button"
                  tabIndex={-1}
                  title={readOnly ? `${WEB_DEV_FILE_LABELS[tab]} is read-only` : undefined}
                  style={{
                    padding: '6px 14px',
                    fontSize: '0.8rem',
                    fontWeight: 600,
                    border: '1px solid #e5e5e5',
                    borderBottom: tab === activeTab ? '1px solid #ffffff' : 'none',
                    borderRadius: '6px 6px 0 0',
                    backgroundColor: tab === activeTab ? '#ffffff' : '#fafafa',
                    color: tab === activeTab ? (readOnly ? '#9ca3af' : '#171717') : '#737373',
                    cursor: 'pointer',
                    marginBottom: '-1px',
                    zIndex: tab === activeTab ? 2 : 1,
                    textTransform: 'uppercase',
                    outline: 'none',
                    transition: 'all 0.15s ease',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px'
                  }}
                >
                  {WEB_DEV_FILE_LABELS[tab]}
                  {readOnly && <span aria-hidden="true" style={{ fontSize: '0.7rem' }}>🔒</span>}
                </button>
              )
            })}
          </div>
        </div>
        <ResetTemplateButton />
      </div>
      <div style={{ width: '100%', flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
        <EditorBody file={activeTab} code={files[activeTab]} editorKey={`${keyPrefix}-${activeTab}`} />
      </div>
    </div>
  )
}

function ColumnsEditor({ keyPrefix, files, enabledFiles, stacked = false, paneTitle = null }) {
  return (
    <div className="ws-editor-container ws-editor-columns" style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <div className="ws-editor-tabs" style={{
        display: 'flex',
        background: '#fafafa',
        borderBottom: '1px solid #e5e5e5',
        padding: '6px 8px',
        justifyContent: 'space-between',
        alignItems: 'center',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          {stacked && paneTitle && (
            <span style={{ fontSize: '0.95rem', fontWeight: 700, color: '#171717', whiteSpace: 'nowrap' }}>
              {paneTitle}
            </span>
          )}
          {!stacked && (
            <div style={{ display: 'flex', gap: '4px' }}>
              {ALL_WEB_DEV_FILES.map((file) => (
                <button
                  key={file}
                  type="button"
                  tabIndex={-1}
                  title={`Hide ${WEB_DEV_FILE_LABELS[file]}`}
                  style={{
                    padding: '5px 12px',
                    fontSize: '0.75rem',
                    fontWeight: 600,
                    border: '1px solid #d1d5db',
                    borderRadius: '4px',
                    backgroundColor: '#171717',
                    color: '#ffffff',
                    cursor: 'pointer',
                    outline: 'none',
                    textTransform: 'uppercase',
                    letterSpacing: '0.03em',
                    transition: 'all 0.15s ease'
                  }}
                >
                  {WEB_DEV_FILE_LABELS[file]}
                </button>
              ))}
            </div>
          )}
        </div>
        <ResetTemplateButton columns />
      </div>
      <div style={{ display: 'flex', flexDirection: stacked ? 'column' : 'row', flex: 1, minHeight: 0 }}>
        {ALL_WEB_DEV_FILES.map((file, index) => {
          const isFirstVisible = index === 0
          const enabled = enabledFiles.includes(file)
          return (
            <div
              key={`${file}-pane-wrapper`}
              style={{
                display: 'flex',
                flex: 1,
                minWidth: stacked ? undefined : 0,
                minHeight: stacked ? 0 : undefined,
                flexDirection: 'column',
                borderLeft: !stacked && !isFirstVisible ? '1px solid #e5e5e5' : 'none',
                borderTop: stacked && !isFirstVisible ? '1px solid #e5e5e5' : 'none',
              }}
            >
              <div style={{
                padding: '6px 12px',
                fontSize: '0.75rem',
                fontWeight: 700,
                letterSpacing: '0.03em',
                textTransform: 'uppercase',
                color: enabled ? '#171717' : '#9ca3af',
                background: '#fafafa',
                borderBottom: '1px solid #e5e5e5',
              }}>
                {WEB_DEV_FILE_LABELS[file]}
                {!enabled && <span aria-hidden="true" style={{ marginLeft: '4px', fontSize: '0.7rem' }}>🔒</span>}
              </div>
              <div style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                <EditorBody file={file} code={files[file]} editorKey={`${keyPrefix}-${file}`} />
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------------------
// Terminal.jsx replica, in its initial (never run) state, plus Workspace's resizer bar.
// ---------------------------------------------------------------------------------------
function TerminalReplica() {
  return (
    <>
      <div className="ws-terminal-panel" style={{ height: `${TERMINAL_HEIGHT}px` }}>
        <div className="ws-terminal-controls">
          <div className="ws-controls-left">
            <button type="button" tabIndex={-1} className="ws-run-button">
              <Play className="ws-icon-sm" />
              Run Tests
            </button>
          </div>
        </div>
        <div className="ws-terminal-output">
          <div className="ws-output-content">
            {"Click 'Run Tests' to execute your code..."}
          </div>
        </div>
      </div>
      <div
        className="ws-resizer ws-vertical"
        style={{ position: 'absolute', bottom: TERMINAL_HEIGHT, left: 0, right: 0, zIndex: 30 }}
      />
    </>
  )
}

// ---------------------------------------------------------------------------------------
// ChatWindow.jsx replica: welcome message only (ChatContext always prepends it), empty
// input. `withCamera` mirrors the screenshot button ChatWindow shows whenever Workspace
// passes a capture handler (always, for GUI sessions; never for code sessions, so the
// Session Setting tab's code workspace passes withCamera={false}).
// ---------------------------------------------------------------------------------------
export function ChatReplica({ withCamera = true }) {
  // ChatContext stamps WELCOME_MESSAGE with the clock time at load and ChatWindow shows it
  // as "hh:mm AM" (formatTime). Read once at mount - no timer, nothing written.
  const [welcomeTime] = useState(() => new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }))
  return (
    <div className="ws-chat-container">
      <div className="ws-chat">
        <div className="ws-chat-history">
          <div className="ws-message">
            <div className="ws-message-info">
              <Avatar sx={{ bgcolor: '#1976d2', width: 38, height: 38 }}>
                <SmartToy sx={{ fontSize: '1rem' }} />
              </Avatar>
              <span className="ws-info-name">AI Assistant</span>
              <span className="ws-info-time">{welcomeTime}</span>
            </div>
            <div className="ws-message-content-wrapper">
              <div className="ws-message-content">
                <pre style={{ whiteSpace: 'pre-wrap', fontFamily: 'inherit' }}>{WELCOME_TEXT}</pre>
              </div>
              <button
                type="button"
                tabIndex={-1}
                title="Copy this response"
                style={{
                  display: 'flex', alignItems: 'center', gap: '4px', marginTop: '4px',
                  padding: '3px 8px', fontSize: '0.72rem', fontWeight: 600,
                  border: '1px solid #e5e5e5', borderRadius: '4px', background: '#ffffff',
                  color: '#4b5563', cursor: 'pointer',
                }}
              >
                <ContentCopyOutlined style={{ fontSize: '0.85rem' }} /> Copy
              </button>
            </div>
          </div>
        </div>

        {withCamera && <div style={{ padding: '0 12px', background: '#ffffff' }} />}

        <form className="ws-message-form" onSubmit={(e) => e.preventDefault()}>
          {withCamera && (
            <div style={{ display: 'flex', gap: '2px' }}>
              <button
                type="button"
                tabIndex={-1}
                title="Attach a screenshot of your workspace"
                style={{
                  background: 'none', border: 'none', cursor: 'pointer',
                  color: '#4b5563', opacity: 1,
                  display: 'flex', alignItems: 'center', padding: '6px',
                }}
              >
                <PhotoCameraOutlined style={{ fontSize: '1.15rem' }} />
              </button>
            </div>
          )}
          <textarea
            className="ws-message-input"
            placeholder="Ask a question..."
            rows={1}
            value=""
            readOnly
            tabIndex={-1}
          />
          <div className="ws-char-count">
            <span style={{ color: '#999', fontWeight: 'normal' }}>0/500</span>
          </div>
          <button type="submit" tabIndex={-1} className="ws-send-button" disabled style={{ opacity: 0.5 }}>
            Send
          </button>
        </form>
      </div>
    </div>
  )
}

// Workspace.jsx's "Workspace View" toggle header (GUI session with chat on), Live Preview
// tab selected - the initial rightPanelTab. activeTab/onSelect are optional: with onSelect
// the tabs switch ('preview' | 'ai'), as in the wizard's interactive preview.
export function RightPanelTabs({ previewTabLabel, activeTab = 'preview', onSelect }) {
  const tabStyle = (active) => ({
    padding: '4px 12px',
    fontSize: '0.75rem',
    fontWeight: 600,
    border: 'none',
    borderRadius: '4px',
    backgroundColor: active ? '#ffffff' : 'transparent',
    color: active ? '#171717' : '#737373',
    boxShadow: active ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
    cursor: 'pointer',
    transition: 'all 0.15s ease'
  })
  return (
    <div style={{
      display: 'flex',
      background: '#fafafa',
      borderBottom: '1px solid #e5e5e5',
      padding: '8px 16px',
      justifyContent: 'space-between',
      alignItems: 'center'
    }}>
      <span style={{ fontSize: '0.85rem', fontWeight: 600, color: '#737373', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
        Workspace View
      </span>
      <div style={{
        display: 'flex',
        backgroundColor: '#f3f4f6',
        borderRadius: '6px',
        padding: '2px',
        border: '1px solid #e5e5e5'
      }}>
        <button type="button" {...buttonProps(onSelect && (() => onSelect('preview')))} style={tabStyle(activeTab === 'preview')}>
          {previewTabLabel || 'Live Preview'}
        </button>
        <button type="button" {...buttonProps(onSelect && (() => onSelect('ai')))} style={tabStyle(activeTab === 'ai')}>
          AI Assistant
        </button>
      </div>
    </div>
  )
}

// WorkspacePage.jsx's Navbar (guest student, untimed session: no My Exercises button, no
// timers - both render nothing in that case).
export function NavbarReplica() {
  return (
    <AppBar position="static" color="primary" elevation={0}>
      <Toolbar
        disableGutters
        sx={{
          minHeight: '64px',
          paddingLeft: '24px',
          paddingRight: '16px',
          justifyContent: 'space-between',
        }}
      >
        <Typography
          variant="h6"
          noWrap
          component="div"
          sx={{
            fontFamily: 'monospace',
            fontWeight: 700,
            letterSpacing: '.1rem',
            color: 'inherit',
            textDecoration: 'none',
            cursor: 'pointer',
            userSelect: 'none'
          }}
        >
          VizPI
        </Typography>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <img
            src={hokieLogo}
            alt="Virginia Tech"
            style={{ height: '40px', width: 'auto', display: 'block' }}
          />
        </div>
      </Toolbar>
    </AppBar>
  )
}

// testCode: the session's saved test_code, when there is one. getGuiSessionConfig prefers
// an instructor-saved starter project, check list and target over the type defaults - the
// same thing the real Workspace does - so passing it shows the exercise as authored. Before
// the session exists (the wizard's Language step) it is null and the type defaults show.
export default function WorkspacePreview({ type, task = '', testCode = null, chatEnabled = true, testsEnabled = true }) {
  const guiConfig = useMemo(() => getGuiSessionConfig({ type, task, test_code: testCode }), [type, task, testCode])
  const starterFiles = useMemo(() => parseFiles(guiConfig?.starterCode), [guiConfig])

  // Reference renders (compare layouts) - memoized so their iframes are not rebuilt on
  // unrelated prop changes (e.g. typing in the wizard's task field).
  const initialFiles = useMemo(() => ({ html: starterFiles.html, css: starterFiles.css, js: '' }), [starterFiles])
  const targetFiles = useMemo(() => ({ html: guiConfig?.finalHtml || '', css: starterFiles.css, js: '' }), [guiConfig, starterFiles])
  const targetJsFiles = useMemo(() => ({ html: starterFiles.html, css: starterFiles.css, js: guiConfig?.finalJs || '' }), [guiConfig, starterFiles])

  if (!guiConfig) {
    return (
      <div className="svw-root">
        <div className="svw-unsupported">No workspace preview for this session type.</div>
      </div>
    )
  }

  const layout = guiConfig.layout
  const isColumnsLayout = layout === 'columns'
  const isCompareLayout = layout === 'compare'
  const isCompareImageLayout = layout === 'compare-image'
  const isCompareJsLayout = layout === 'compare-js'
  const isAnyCompare = isCompareLayout || isCompareImageLayout || isCompareJsLayout
  const hidesGradingPanels = isColumnsLayout || isAnyCompare

  // Real Workspace: session.task || guiConfig.assignment.
  const taskText = task || guiConfig.assignment
  const enabledFiles = guiConfig.enabledFiles || ALL_WEB_DEV_FILES
  const renderLabel = guiConfig.previewMode === 'manual' ? guiConfig.previewLabel : null
  const testCases = (guiConfig.tests || []).map((testName) => ({ testName, result: null }))
  const editorKey = `svw-${guiConfig.type}`

  return (
    <div className="svw-root">
      <NavbarReplica />
      <div className="svw-page-body">
        <div className="ws-container">
          <div className="ws-content">

            {/* LEFT PANEL - graded types: Task Description + Test Cases */}
            {!hidesGradingPanels && (
              <>
                <div className="ws-panel-left" style={{ width: `${LEFT_WIDTH}px` }}>
                  <div className="ws-panel-content">
                    <ReplayTaskDescription task={taskText} />
                    {testsEnabled && (
                      <>
                        <div className="ws-section-divider"></div>
                        <ReplayTestCases testCases={testCases} />
                      </>
                    )}
                  </div>
                </div>
                <div className="ws-resizer ws-horizontal" />
              </>
            )}

            {/* LEFT PANEL - InitialFinalJS: stacked "Your Code" editor */}
            {isCompareJsLayout && (
              <>
                <div className="ws-panel-left" style={{ width: `${LEFT_WIDTH}px`, display: 'flex', flexDirection: 'column' }}>
                  <ColumnsEditor keyPrefix={editorKey} files={starterFiles} enabledFiles={enabledFiles} stacked paneTitle="Your Code" />
                </div>
                <div className="ws-resizer ws-horizontal" />
              </>
            )}

            {/* MIDDLE PANEL */}
            <div className="ws-middle-panel">
              {isCompareLayout && (
                <>
                  <div style={{ padding: '12px 16px 0' }}>
                    <ReplayTaskDescription task={taskText} />
                  </div>
                  <div style={{ display: 'flex', alignItems: 'stretch', gap: '1px', backgroundColor: '#e5e5e5', borderTop: '1px solid #e5e5e5', borderBottom: '1px solid #e5e5e5', height: '38%', minHeight: '160px' }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <PreviewPane title="Initial" badge={null} showRefresh={false} files={initialFiles} />
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '32px', flexShrink: 0, backgroundColor: '#ffffff' }}>
                      <ArrowForward style={{ color: '#9ca3af', fontSize: '1.5rem' }} />
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <PreviewPane title="Target" badge={null} showRefresh={false} files={targetFiles} />
                    </div>
                  </div>
                </>
              )}

              {isCompareJsLayout && (
                <div style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, overflowY: 'auto' }}>
                  <div style={{ borderBottom: '1px solid #e5e5e5', flexShrink: 0 }}>
                    <div style={{ display: 'flex', justifyContent: 'flex-end', padding: '6px 16px 0' }}>
                      <button
                        type="button"
                        tabIndex={-1}
                        style={{
                          background: 'none', border: 'none', cursor: 'pointer', padding: '2px 4px',
                          fontSize: '0.75rem', fontWeight: 600, color: '#2563eb',
                        }}
                      >
                        Hide task ▴
                      </button>
                    </div>
                    <div style={{ padding: '0 16px 12px' }}>
                      <ReplayTaskDescription task={taskText} />
                    </div>
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', flex: '1 0 480px' }}>
                    <div style={{ height: `${JS_VIEW_SPLIT_PERCENT}%`, minHeight: 0, borderBottom: '1px solid #e5e5e5' }}>
                      <PreviewPane title="Target" badge="Interactive" showRefresh={false} chromeStyle files={targetJsFiles} />
                    </div>
                    <div title="Drag to resize" style={{ height: '6px', flexShrink: 0, cursor: 'row-resize', backgroundColor: '#e5e5e5' }} />
                    <div style={{ height: `${100 - JS_VIEW_SPLIT_PERCENT}%`, minHeight: 0 }}>
                      <PreviewPane title="Your View" badge="Interactive" chromeStyle renderLabel={renderLabel} />
                    </div>
                  </div>
                </div>
              )}

              {isCompareImageLayout && (
                <div style={{ display: 'flex', alignItems: 'stretch', gap: '1px', backgroundColor: '#e5e5e5', borderBottom: '1px solid #e5e5e5', height: '38%', minHeight: '200px' }}>
                  <div style={{ flex: 1, minWidth: 0, padding: '12px 16px', overflow: 'auto', backgroundColor: '#ffffff' }}>
                    <ReplayTaskDescription task={taskText} />
                  </div>
                  <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 16px', borderBottom: '1px solid #e5e5e5', backgroundColor: '#ffffff' }}>
                      <span style={{ fontFamily: "'Open Sans', sans-serif", fontWeight: 600, fontSize: '0.9rem', color: '#171717' }}>Target</span>
                    </div>
                    <div style={{ flex: 1, overflow: 'auto', display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: '#f9fafb' }}>
                      {guiConfig.finalImage ? (
                        <img
                          src={guiConfig.finalImage}
                          alt="Target layout the student is trying to reach"
                          style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
                        />
                      ) : (
                        <div style={{ color: '#9ca3af', fontSize: '0.85rem', padding: '16px', textAlign: 'center' }}>
                          No target image set. Ask your instructor to add one in the exercise editor.
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {isCompareLayout ? (
                <div style={{ display: 'flex', flex: 1, minHeight: 0 }}>
                  <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
                    <TabbedEditor keyPrefix={editorKey} files={starterFiles} enabledFiles={enabledFiles} hideDisabledTabs paneTitle="Your Code" />
                  </div>
                  <div style={{ flex: 1, minWidth: 0, borderLeft: '1px solid #e5e5e5' }}>
                    <PreviewPane renderLabel={renderLabel} />
                  </div>
                </div>
              ) : isCompareImageLayout ? (
                <div style={{ display: 'flex', flex: 1, minHeight: 0 }}>
                  <div style={{ width: `${IDE_SPLIT_PERCENT}%`, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
                    <TabbedEditor
                      keyPrefix={editorKey}
                      files={starterFiles}
                      enabledFiles={enabledFiles}
                      hideDisabledTabs
                      extraReadOnlyFiles={['html']}
                      paneTitle="Your CSS"
                    />
                  </div>
                  <div title="Drag to resize" style={{ width: '6px', flexShrink: 0, cursor: 'col-resize', backgroundColor: '#e5e5e5' }} />
                  <div style={{ width: `${100 - IDE_SPLIT_PERCENT}%`, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
                    <PreviewPane title="Your View" badge="Drag divider to resize" showRefresh={false} renderLabel={renderLabel} />
                  </div>
                </div>
              ) : isCompareJsLayout ? null : isColumnsLayout ? (
                <ColumnsEditor keyPrefix={editorKey} files={starterFiles} enabledFiles={enabledFiles} />
              ) : (
                <TabbedEditor keyPrefix={editorKey} files={starterFiles} enabledFiles={enabledFiles} />
              )}

              {!hidesGradingPanels && <TerminalReplica />}
            </div>

            {/* Right resizer + RIGHT PANEL: always mounted for GUI sessions
                (chatEnabled || isYCHFall2026, and every GUI type sets isYCHFall2026). */}
            <div className="ws-resizer ws-horizontal" />

            {isAnyCompare ? (
              // Chat-only column; with chat off the real Workspace leaves it empty.
              <div className="ws-panel-right" style={{ width: `${RIGHT_WIDTH}px`, display: 'flex', flexDirection: 'column' }}>
                {chatEnabled && (
                  <div style={{ flex: 1, minHeight: 0 }}>
                    <ChatReplica />
                  </div>
                )}
              </div>
            ) : (
              // Live Preview (selected tab); ChatWindow is mounted but display:none in the
              // real page, so it is not rendered here.
              <div className="ws-panel-right" style={{ width: `${RIGHT_WIDTH}px`, display: 'flex', flexDirection: 'column' }}>
                {chatEnabled && <RightPanelTabs previewTabLabel={guiConfig.previewTabLabel} />}
                <div style={{ display: 'contents' }}>
                  <PreviewPane renderLabel={renderLabel} />
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
